import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { useRef } from 'react'
import { badgesQuery } from '@/entities/badge'
import { NewBookingButton } from '@/features/booking-create'
import { Skeleton } from '@/shared/ui'
import { useNavIndicator } from '../lib/use-nav-indicator'
import { NavIndicator } from './NavIndicator'
import s from './NavBar.module.css'

/**
 * Пять разделов приложения. Порядок и подписи — из макета (строки 233–237).
 *
 * `search` в ссылках НЕ передаётся намеренно. Глобальный параметр `booking` (открытая карточка
 * брони) при переходе отваливается, и модалка закрывается — так и надо: открыл бронь на
 * шахматке, нажал «Задачи» — карточка не должна ехать за тобой на соседний экран. Обратное
 * правило (смена фильтра ВНУТРИ экрана обязана сохранять `booking`) объяснено в комментарии
 * к `rootSearchSchema`, `app/router/search.ts`.
 */
const TABS = [
  { to: '/dashboard', label: 'Сводка' },
  { to: '/chess', label: 'Шахматка' },
  { to: '/bookings', label: 'Брони' },
  { to: '/tasks', label: 'Задачи' },
  { to: '/inbox', label: 'Чаты' },
] as const

export function NavBar() {
  const tabsRef = useRef<HTMLDivElement>(null)
  const { data: badges } = useQuery(badgesQuery())

  const pathname = useRouterState({ select: (state) => state.location.pathname })

  // Активный таб считаем сами, а не ждём `data-status` от `Link`: индекс нужен замеру
  // СИНХРОННО, на первой же отрисовке. `-1` — мы в Студии: её открывают шестерёнкой, своего
  // таба у неё нет, полосы быть не должно.
  const activeIndex = TABS.findIndex((tab) => pathname.startsWith(tab.to))

  // Хук зовётся ЗДЕСЬ, а не внутри `NavIndicator`, и это не стилистика: индикатор — потомок
  // контейнера, а layout-эффекты потомков React выполняет РАНЬШЕ, чем прикрепляет ref
  // родителю. Замер внутри индикатора видел бы `null` ровно в единственный важный момент.
  // Подробности — в комментарии к `useNavIndicator`.
  const indicator = useNavIndicator(tabsRef, activeIndex)

  return (
    <nav className={s.root}>
      <div className={s.tabs} ref={tabsRef}>
        {TABS.map((tab, index) => (
          <Link key={tab.to} to={tab.to} data-tab={index} className={s.tab}>
            {tab.label}

            {/* Бейдж и точка — только когда сервер прислал ненулевое значение. Пока `/badges`
                летит, места они не занимают: ширина таба меняется, и полосу под ним
                пересчитает ResizeObserver в `NavIndicator`. */}
            {tab.to === '/tasks' && badges && badges.tasks > 0 && (
              <span className={s.count}>{badges.tasks}</span>
            )}

            {/* Точка нарисована CSS и для скринридера пуста — смысл несёт скрытый текст рядом.
                Он дописывается к имени ссылки: «Чаты, есть непрочитанные». Без него состояние
                видел бы только зрячий, а бейдж «Задачи» рядом свой счётчик как раз объявляет —
                он обычный текст. */}
            {/* Сравнение с нулём — не придирка: `badges?.inboxAwaiting` это ЧИСЛО, и на `&&`
                нулевое значение React печатает как текст — рядом со вкладкой появлялся бы голый «0». */}
            {tab.to === '/inbox' && (badges?.inboxAwaiting ?? 0) > 0 && (
              <>
                <span className={s.dot} aria-hidden="true" />
                <span className={s.srOnly}>, есть ждущие ответа</span>
              </>
            )}
          </Link>
        ))}

        <NavIndicator position={indicator} />
      </div>

      <div className={s.aside}>
        {/* Дата — с сервера (`todayLabel`), а не из браузера. У компании свой часовой пояс:
            менеджер во Владивостоке в час ночи увидел бы здесь «4 июля», пока в шахматке
            колонка «сегодня» стоит на «3 июля» по времени объекта в Сочи. */}
        {badges ? (
          <span className={s.today}>{badges.todayLabel}</span>
        ) : (
          <Skeleton width={78} height={12} radius={4} />
        )}

        {/*
         * Кнопка вместе со своей модалкой — фича (`features/booking-create`), а не разметка шапки:
         * форма создания знает про контракт, про ключ идемпотентности и про рубли-в-копейках, и
         * шапке всё это не принадлежит. Виджет отдаёт ей только геометрию своего места
         * (`s.newBooking` — h32, radius 7, 13px/550): общий компонент не владеет своим внешним
         * видом в чужой раскладке, соседей знает только тот, кто их composes.
         *
         * Открывается модалка ЛОКАЛЬНЫМ состоянием, а не search-параметром, — разбор в самой
         * фиче. Коротко: `?booking=` называет ресурс, которым делятся; пустая форма не называет
         * ничего, а «назад» браузера стирал бы несохранённый ввод.
         */}
        <NewBookingButton className={s.newBooking} />
      </div>
    </nav>
  )
}
